import { setToken, getToken } from 'api/utils/tokenManagement';

import { logOutOperation } from 'store/auth/operations';

import { ERROR_LEVELS, SENTRY_AXIOS } from 'services/Sentry/constants';
import { UNAUTHORIZED_TOKEN, UNAUTHORIZED_USER } from './constants';

import sentry from 'services/Sentry/SentryInstance';

import ApiMethods, { ApiErrorHandler } from './ApiMethods';
import { logger } from '@user-portal/logger';

export interface ClientError extends Error {
    response?: {
        status?: number;
        data?: any;
    };
}

export function isApiClientError(error: unknown): error is ClientError {
    return (
        error instanceof Error &&
        typeof (error as ClientError).response === 'object' &&
        (error as ClientError).response !== null
    );
}

const MAX_RETRIES = 3;

interface InitApiClientParams {
    apiUrl?: string;
    apiVersion?: string;
    apiKey?: string;
    retries?: number;
    timeout?: number;
    requestInterceptors?: Array<(config: any) => any>;
    getAuthToken?: () => string | null | undefined;
    onUpdateToken?: (token: string) => void;
    onAuthorisationError?: (error: any) => void;
    onErrorHandler?: ApiErrorHandler;
}

export function initApiClient({
    apiUrl = import.meta.env.VITE_WF_API_URL,
    apiVersion = import.meta.env.VITE_API_VERSION,
    apiKey = import.meta.env.VITE_WF_API_KEY,
    retries = MAX_RETRIES,
    timeout = 35000,
    requestInterceptors = [],
    getAuthToken = getToken,
    onUpdateToken = setToken,
    onAuthorisationError,
    onErrorHandler,
}: InitApiClientParams = {}) {
    const sharedConfig = {
        apiUrl,
        apiVersion,
        apiKey,
        retries,
        timeout,
        requestInterceptors,
        getAuthToken,
        onUpdateToken,
        onAuthorisationError,
        onError: onErrorHandler,
    };

    return new ApiMethods({ ...sharedConfig, isPrivate: true });
}

function ApiClient() {
    return initApiClient({
        apiUrl: import.meta.env.VITE_WF_API_URL,
        apiVersion: import.meta.env.VITE_API_VERSION,
        apiKey: import.meta.env.VITE_WF_API_KEY,
        retries: MAX_RETRIES,
        timeout: 35000,
        requestInterceptors: [
            (config) => {
                config.headers['project-id'] = 'menscoach';

                return config;
            },
        ],
        getAuthToken: () => getToken(),
        onUpdateToken: (token) => {
            setToken(token);
        },
        onAuthorisationError: (error: ClientError) => {
            const errorStatus = error.response?.status;

            if (errorStatus && [UNAUTHORIZED_USER, UNAUTHORIZED_TOKEN].includes(errorStatus)) {
                logOutOperation();
            }
        },
        onErrorHandler: ({ extras, status, message, error }) => {
            const level = Number(status) >= 500 ? ERROR_LEVELS.CRITICAL : ERROR_LEVELS.ERROR;

            sentry.logError(new Error(message), SENTRY_AXIOS, level, { response: error?.response, ...extras });
            logger.error('API Client Error:', { status, message, error, ...extras });
        },
    });
}

export default ApiClient();
